import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      
      // Check for pending team invite
      const pendingInviteId = sessionStorage.getItem('pendingInviteId');
      if (pendingInviteId) {
        // Redirect to invite acceptance page
        navigate(`/invite/${pendingInviteId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="font-bold text-2xl text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
              Strategic Architect
            </span>
          </div>

          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
            Create your account
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            Start your strategic planning journey today.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-600 dark:text-slate-300 font-medium">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:border-[#26C6DA] focus:ring-1 focus:ring-[#26C6DA] rounded-lg"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600 dark:text-slate-300 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:border-[#26C6DA] focus:ring-1 focus:ring-[#26C6DA] rounded-lg"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600 dark:text-slate-300 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:border-[#26C6DA] focus:ring-1 focus:ring-[#26C6DA] rounded-lg pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:text-slate-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Must be at least 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full py-6 shadow-lg hover:shadow-xl transition-all"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-slate-500 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#26C6DA] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
